import type { FC } from 'react'

import { useEffect } from 'react'
import { useRouter } from 'next/router'

/**
 * Watches every element with data-section-tone and copies the tone of the one
 * crossing the middle of the viewport onto <html data-tone>. The registered
 * --tone-* properties in globals.css then blend into the new environment.
 */
export const ToneController: FC = () => {
    const { asPath } = useRouter()

    useEffect(() => {
        const root = document.documentElement
        const sections = Array.from(document.querySelectorAll<HTMLElement>('[data-section-tone]'))

        if (!sections.length) {
            root.dataset.tone = 'ivory'
            return
        }

        // Arrive in the right environment at once; only later changes blend.
        const middle = window.innerHeight / 2
        const current = sections.find(section => {
            const box = section.getBoundingClientRect()

            return box.top <= middle && box.bottom >= middle
        }) ?? sections[0]

        root.dataset.toneInstant = ''
        root.dataset.tone = current.dataset.sectionTone ?? 'ivory'

        const release = window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => delete root.dataset.toneInstant)
        })

        const observer = new IntersectionObserver(entries => {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue

                const tone = (entry.target as HTMLElement).dataset.sectionTone

                if (tone && root.dataset.tone !== tone) root.dataset.tone = tone
            }
        }, { rootMargin: '-49% 0px -50% 0px', threshold: 0 })

        sections.forEach(section => observer.observe(section))

        return () => {
            window.cancelAnimationFrame(release)
            delete root.dataset.toneInstant
            observer.disconnect()
        }
    }, [asPath])

    return null
}
